/**
 * THIS LEAGUE.
 *
 * Everything here is a fact about a particular group of friends rather than
 * about fantasy football, and this is the only file that has to change to point
 * the app at a different league. The engine reads it; nothing else hardcodes
 * any of it.
 */
const DEFAULT_PAYOUTS = {
    buyIn: 300,
    managers: 12,
    regularSeasonWeeks: 14,
    awards: [
        {
            id: 'weeklyHighScore',
            label: 'Weekly High Score',
            description: 'Most points scored in a given week.',
            amount: 30,
            manual: false,
            perWeek: true,
            compute: { kind: 'weeklyExtreme', metric: 'pointsFor', pick: 'high' },
        },
        {
            id: 'goon',
            label: 'Goon',
            description: 'Most total points scored in the regular season.',
            amount: 300,
            manual: false,
            compute: { kind: 'seasonTotal', metric: 'pointsFor', pick: 'high' },
            notify: {
                onLeadChange: {
                    title: 'New Goon',
                    verb: 'has the most points now',
                    url: '/standings',
                },
            },
        },
        {
            id: 'survivor',
            label: 'Survivor',
            description: 'Lowest scorer is eliminated each week. Last manager standing wins.',
            amount: 100,
            manual: false,
            compute: { kind: 'survivor' },
            page: { nav: 'Survivor' },
        },
        {
            id: 'unlucky',
            label: 'Unlucky',
            description: 'Most total points scored against them in the regular season.',
            amount: 25,
            manual: false,
            compute: { kind: 'seasonTotal', metric: 'pointsAgainst', pick: 'high' },
        },
        {
            id: 'heatbreaker',
            label: 'Heatbreaker',
            description: 'Most losses by 5 points or fewer in the regular season.',
            amount: 25,
            manual: false,
            compute: { kind: 'closeLosses', threshold: 5 },
        },
        {
            id: 'firstRoundClown',
            label: 'First Round Clown',
            description: 'Lowest-scoring first-round pick over the season.',
            amount: 25,
            manual: false,
            compute: {
                kind: 'draftPickExtreme',
                round: 1,
                pick: 'low',
                eligibility: 'manual',
            },
            notify: {
                onLeadChange: {
                    title: 'New First Round Clown',
                    verb: 'owns it now',
                    url: '/award/firstRoundClown',
                },
            },
            page: { nav: 'First Round Clown' },
        },
        // Last, and it matters: the board emits awards in this order, so the ticket
        // strip reads the same as it always has.
        {
            id: 'busted',
            label: 'Busted',
            description: 'First manager to have a player arrested. Player must be ON the roster at the time.',
            // $30 in the $300 era — confirmed by the 2024 board ($1,908 1st place
            // only reconciles with a $30 Busted; $25 would give $1,913).
            amount: 30,
            manual: true,
            compute: { kind: 'manual' },
        },
    ],
    placements: [
        { place: 1, label: '1st', kind: 'remaining', fraction: 1 },
        { place: 2, label: '2nd', kind: 'fixed', amount: 468 },
        { place: 3, label: '3rd', kind: 'fixed', amount: 299 },
    ],
};
/**
 * Override specific award amounts, and optionally drop awards entirely.
 *
 * The engine's AwardId is an open string — it has no opinion about which pots
 * exist — so narrowing to this league's own vocabulary here is what gets the
 * compiler to check that a season's table names awards that are really in the
 * default list.
 */
function withAwards(base, amounts, keepOnly) {
    const idOf = (a) => a.id;
    return base.awards
        .filter((a) => !keepOnly || keepOnly.includes(idOf(a)))
        .map((a) => (idOf(a) in amounts ? { ...a, amount: amounts[idOf(a)] } : a));
}
/**
 * Payout rules per season, reconciled against the real payout sheets. Every
 * board below has been verified to reproduce its actual 1st-place dollar figure
 * to the cent.
 *
 * The rules genuinely evolved, so nothing is assumed:
 *  - buy-in $250 -> $300 (2024)
 *  - weekly & Goon $25/$250 -> $30/$300 (2024)
 *  - survivor $50 -> $100, unlucky $50 -> $25 (2023)
 *  - Heatbreaker, First Round Clown and Busted added in 2023
 *  - Busted $25 -> $30 (2024)
 *  - placement structure changed: 2022 split 1st/2nd 60/40 of the remainder
 *    with 3rd fixed; 2023+ give 1st the whole remainder with 2nd/3rd fixed
 *
 * Reconciliation:
 *  - 2022: 1st $1,230, 2nd $820, 3rd $250  (pot $3,000) ✓
 *  - 2023: 1st $1,590                       (pot $3,000) ✓
 *  - 2024: 1st $1,908                       (pot $3,600) ✓
 *  - 2025: 1st $1,938                       (pot $3,600) ✓
 *  - 2026: confirmed to match 2025
 */
const SEASON_PAYOUTS = {
    // Only 4 awards existed; placements split the remainder 60/40 with 3rd fixed.
    2022: {
        ...DEFAULT_PAYOUTS,
        buyIn: 250,
        awards: withAwards(DEFAULT_PAYOUTS, { weeklyHighScore: 25, goon: 250, survivor: 50, unlucky: 50 }, ['weeklyHighScore', 'goon', 'survivor', 'unlucky']),
        placements: [
            { place: 3, label: '3rd', kind: 'fixed', amount: 250 },
            { place: 1, label: '1st', kind: 'remaining', fraction: 0.6 },
            { place: 2, label: '2nd', kind: 'remaining', fraction: 0.4 },
        ],
    },
    2023: {
        ...DEFAULT_PAYOUTS,
        buyIn: 250,
        awards: withAwards(DEFAULT_PAYOUTS, {
            weeklyHighScore: 25,
            goon: 250,
            busted: 25,
        }),
        placements: [
            { place: 1, label: '1st', kind: 'remaining', fraction: 1 },
            { place: 2, label: '2nd', kind: 'fixed', amount: 360 },
            { place: 3, label: '3rd', kind: 'fixed', amount: 250 },
        ],
    },
    2024: DEFAULT_PAYOUTS,
    2025: DEFAULT_PAYOUTS,
    2026: DEFAULT_PAYOUTS,
};
export const LEAGUE = {
    leagueId: 276366,
    name: 'Weeeeed Is Tight',
    bookName: 'The Book',
    mark: 'D7',
    dedication: 'In memory of Tanner Harrison, 1994\u20132026.',
    // 2022 is the earliest season ESPN will serve without auth for this league;
    // 2018-2021 return 401.
    firstSeason: 2022,
    deployment: {
        // A named database inside a project shared with another app, which is why
        // the auth pool is shared and the rules can never gate on request.auth
        // alone. A league standing this up fresh wants its own project and the
        // '(default)' database.
        firebaseProjectId: 'bowl-bonanza-a09ed',
        firestoreDatabaseId: 'fantasy-football',
        vapidSubject: 'mailto:julian@julianfife.com',
    },
    defaults: DEFAULT_PAYOUTS,
    seasons: SEASON_PAYOUTS,
};
//# sourceMappingURL=config.js.map